import { useRef, useState } from "react";
import useSWR from "swr";
import { Plus, Trash2, Upload, Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg } from "@/lib/api";
import { usd, fmtDate, pct } from "@/lib/format";
import { Card, btn, inputCls } from "@/components/common/Ornament";
import { TemplateLinks } from "./FinancialsTab";

const FIELDS = [
  ["property_name", "Property name", "text"], ["address", "Address", "text"], ["sale_date", "Sale date", "date"],
  ["sale_price", "Sale price (USD)", "number"], ["rooms", "Rooms", "number"], ["hotel_class", "Hotel class / brand", "text"],
  ["cap_rate", "Cap rate % (optional)", "number"], ["source", "Source", "text"], ["lat", "Latitude (map)", "number"],
  ["lng", "Longitude (map)", "number"], ["notes", "Notes", "text"],
];
const BLANK = { property_name: "", address: "", sale_date: "", sale_price: "", rooms: "", hotel_class: "", cap_rate: "", notes: "", source: "", lat: "", lng: "", is_sample: false };

function CompDialog({ comp, onClose, onSaved }) {
  const [f, setF] = useState({ ...BLANK, ...comp });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const n = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
    const body = { ...f, sale_price: Number(f.sale_price), rooms: Number(f.rooms), cap_rate: n(f.cap_rate), lat: n(f.lat), lng: n(f.lng) };
    delete body.id; delete body.created_at;
    try {
      if (comp.id) await api.put(`/admin/comps/${comp.id}`, body); else await api.post("/admin/comps", body);
      toast.success("Comparable saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="admin-comp-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{comp.id ? "Edit" : "Add"} comparable sale</DialogTitle><DialogDescription>Price per key is calculated automatically.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          {FIELDS.map(([k, l, t]) => (
            <label key={k} className={k === "notes" || k === "property_name" ? "sm:col-span-2" : ""}>
              <span className="text-xs font-semibold text-[#3E5C4E]">{l}</span>
              <input type={t} step="any" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls + " mt-1"} data-testid={`admin-comp-input-${k}`} />
            </label>
          ))}
          <label className="flex items-center gap-2 text-sm sm:col-span-2"><Checkbox checked={f.is_sample} onCheckedChange={(v) => setF({ ...f, is_sample: !!v })} data-testid="admin-comp-input-is_sample" /> Mark as SAMPLE DATA</label>
        </div>
        <button onClick={save} disabled={busy || !f.property_name || !f.sale_price || !f.rooms} className={btn.gold + " mt-2"} data-testid="admin-comp-save-btn">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save</button>
      </DialogContent>
    </Dialog>
  );
}

export default function CompsTab() {
  const { data: comps = [], mutate } = useSWR("/comps");
  const [edit, setEdit] = useState(null);
  const [replace, setReplace] = useState(false);
  const ref = useRef(null);
  const samples = comps.filter((c) => c.is_sample).length;

  const upload = async (file) => {
    const fd = new FormData();
    fd.append("file", file); fd.append("replace_samples", replace);
    try { const { data } = await api.post("/admin/comps/upload", fd); mutate(); toast.success(`${data.added} comparable(s) added`); } catch (e) { toast.error(errMsg(e)); } finally { ref.current.value = ""; }
  };
  const del = async (c) => {
    if (!window.confirm(`Delete “${c.property_name}”?`)) return;
    await api.delete(`/admin/comps/${c.id}`); mutate();
  };
  const delSamples = async () => {
    if (!window.confirm(`Delete all ${samples} SAMPLE rows?`)) return;
    await api.delete("/admin/comps/samples"); mutate(); toast.success("Sample rows removed");
  };

  return (
    <div className="space-y-6">
      <Card eyebrow="Bulk Upload" title="Import Comparable Sales" testId="admin-comps-upload">
        <div className="flex flex-wrap items-center gap-3">
          <TemplateLinks kind="comps" />
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={replace} onCheckedChange={(v) => setReplace(!!v)} data-testid="admin-comps-replace-samples" /> Remove sample rows on import</label>
          <input ref={ref} type="file" accept=".csv,.xlsx" hidden onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="admin-comps-file-input" />
          <button onClick={() => ref.current.click()} className={btn.primary + " !h-9"} data-testid="admin-comps-upload-btn"><Upload className="h-4 w-4" /> Upload CSV / XLSX</button>
        </div>
      </Card>
      <Card eyebrow="Records" title={`Comparable Sales (${comps.length})`} testId="admin-comps"
        right={<div className="flex gap-2">
          {samples > 0 && <button onClick={delSamples} className={btn.danger} data-testid="admin-comps-delete-samples">Delete {samples} sample rows</button>}
          <button onClick={() => setEdit({})} className={btn.primary + " !h-9"} data-testid="admin-comp-add-btn"><Plus className="h-4 w-4" /> Add</button>
        </div>}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead><tr className="border-b border-[#0F2E23] text-left text-xs uppercase tracking-wider text-[#3E5C4E]"><th className="py-2">Property</th><th>Date</th><th className="text-right">Price</th><th className="text-right">Rooms</th><th className="text-right">Per key</th><th className="text-right">Cap</th><th /></tr></thead>
            <tbody>
              {comps.map((c, i) => (
                <tr key={c.id} className="border-b border-[#E2DCD2]" data-testid={`admin-comp-row-${i}`}>
                  <td className="py-2 pr-2">{c.is_sample && <span className="text-[0.6rem] font-bold text-amber-700 mr-1">SAMPLE</span>}{c.property_name.replace("SAMPLE DATA – REPLACE · ", "")}</td>
                  <td>{fmtDate(c.sale_date)}</td>
                  <td className="text-right font-mono-num">{usd(c.sale_price)}</td>
                  <td className="text-right font-mono-num">{c.rooms}</td>
                  <td className="text-right font-mono-num">{usd(c.sale_price / c.rooms)}</td>
                  <td className="text-right font-mono-num">{c.cap_rate !== null && c.cap_rate !== undefined ? pct(c.cap_rate) : "—"}</td>
                  <td className="text-right whitespace-nowrap pl-2">
                    <button onClick={() => setEdit(c)} className="p-2 text-slate-500 hover:text-[#0F2E23]" data-testid={`admin-comp-edit-${i}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => del(c)} className="p-2 text-slate-500 hover:text-red-600" data-testid={`admin-comp-delete-${i}`}><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      {edit && <CompDialog comp={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); mutate(); }} />}
    </div>
  );
}
